
import React, { useState } from 'react';
import { SimulationConfig, MelodyData } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { AUDIO } from './services/AudioService';
import { BALL_SOUND_EFFECTS } from './constants';
import { SETTING_INFO } from './settingInfo';
import { PRESET_LIBRARY } from './presets';

interface RhythmicModeSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  onReset: () => void;
}

export const RhythmicModeSection: React.FC<RhythmicModeSectionProps> = ({ config, setConfig, onReset }) => {
  const [selectedMelodyId, setSelectedMelodyId] = useState<string>(config.selectedMelody || '');
  
  React.useEffect(() => {
    if (config.selectedMelody) {
      setSelectedMelodyId(config.selectedMelody);
    }
  }, [config.selectedMelody]);

  const isRhythmic = config.mode === 'RHYTHMIC' || config.mode === 'RHYTHMIC_ESCAPE';
  const rhythmicMsg = "This setting is designed specifically for Rhythmic/Hybrid modes.";

  const syncMelody = () => {
    if (!selectedMelodyId) return;
    setConfig(prev => ({ ...prev, selectedMelody: selectedMelodyId }));
    AUDIO.resetIndex();
    onReset();
    alert("Rhythmic Sync Updated & Simulation Reset!");
  };

  const breakAnims = [
    'EXPLOSION', 'RING_EXPAND', 'SHATTER', 'GLOW_FADE', 'STARDUST', 'VORTEX', 'PIXEL_BURST', 'SONIC_BOOM', 
    'FIREWORK', 'GHOST_ECHO', 'SHARD_STORM', 'NEBULA', 'DATA_STREAM', 'RIPPLE_WAVE', 'SHOCKWAVE', 'GLITCH_SQUARE', 
    'CONFETTI', 'ENERGY_ORBS', 'FRACTAL_GROWTH', 'RAINBOW_BURST', 'HEXAGON_GRID', 'PLASMA_WAVE', 'SMOKE_RING', 
    'LIGHTNING_STRIKE', 'SPIRAL_STARS', 'CRYSTAL_GROWTH', 'GALAXY_SWIRL', 'PHANTOM_RINGS', 'METEOR_SHOWER', 
    'QUANTUM_LEAP', 'FIRE_SPIRAL', 'AURORA_BOREALIS', 'DEEP_SEA_BUBBLES', 'TIME_WARP', 'PHYSICS_SHATTER', 
    'GRAVITY_CRUMBLE', 'EXPLOSIVE_SHARDS', 'FLOATING_DEBRIS', 'VORTEX_SHATTER', 'MOLTEN_DRIP', 'BOUNCY_CHUNKS', 
    'PIXEL_GRAVITY', 'RADIAL_SLICE', 'IMPLODE_EXPLODE', 'CHAIN_REACTION', 'SHRAPNEL', 'DEBRIS_CLOUD', 
    'ASTEROID_FIELD', 'VOLCANIC_BURST', 'SUPERNOVA_FRAG', 'BLACK_HOLE_EAT', 'NEBULA_DRIFT', 'COMET_TAIL', 'PRISM_SPLIT',
    'GRAVITY_DUST', 'NONE'
  ];

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-pink-600/10 border-pink-500/30 mb-4 shadow-xl">
      <SectionLabel icon="fa-music" darkMode={config.darkMode}>Rhythmic Visualizer Mode</SectionLabel>
      
      <div className={`bg-black/20 p-3 rounded-xl border border-white/10 mb-2 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
        <p className="text-[10px] font-bold opacity-70 mb-2 uppercase tracking-widest">
            Select Song for Rhythmic Sync
        </p>
        
        <select 
            value={selectedMelodyId}
            onChange={(e) => setSelectedMelodyId(e.target.value)}
            className={`w-full p-2 text-[10px] font-mono rounded-lg border outline-none mb-2 ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
        >
            <option value="" className="bg-neutral-900 text-white">-- Choose a Song --</option>
            {config.customMelodies.map(m => (
                <option key={m.id} value={m.name} className="bg-neutral-900 text-white">{m.name} ({m.format})</option>
            ))}
        </select>

        <button 
            onClick={syncMelody} 
            disabled={!selectedMelodyId}
            className={`w-full py-2 rounded-lg text-[10px] font-black uppercase transition-all ${!selectedMelodyId ? 'bg-neutral-600 opacity-50 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-500 text-white'}`}
        >
            Sync Simulation to Song
        </button>
      </div>

      {/* Playlist Section */}
      <div className={`bg-black/20 p-3 rounded-xl border border-white/10 mb-2 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest">
                Playlist Mode (Sequential Play)
            </p>
            <Toggle 
                active={config.playlistEnabled} 
                onClick={() => setConfig(p => ({...p, playlistEnabled: !p.playlistEnabled}))} 
                color="bg-pink-600"
            />
        </div>

        {config.playlistEnabled && (
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <select 
                        id="playlist-song-select"
                        className={`flex-1 p-2 text-[10px] font-mono rounded-lg border outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
                    >
                        <option value="" className="bg-neutral-900 text-white">-- Add Song --</option>
                        {config.customMelodies.map(m => (
                            <option key={m.id} value={m.name} className="bg-neutral-900 text-white">{m.name}</option>
                        ))}
                    </select>
                    <button 
                        onClick={() => {
                            const select = document.getElementById('playlist-song-select') as HTMLSelectElement;
                            const melodyId = select.value;
                            if (melodyId) {
                                setConfig(p => ({
                                    ...p,
                                    playlist: [...p.playlist, { id: Math.random().toString(36).substr(2, 9), melodyId, presetName: 'Default' }]
                                }));
                                select.value = "";
                            }
                        }}
                        className="px-3 py-2 bg-pink-600 rounded-lg text-white text-[10px] font-black"
                    >
                        ADD
                    </button>
                </div>

                <div className="max-h-40 overflow-y-auto flex flex-col gap-1 pr-1">
                    {config.playlist.map((item, index) => (
                        <div key={item.id} className={`flex items-center gap-2 p-2 rounded-lg border ${config.playlistCurrentIndex === index ? 'bg-pink-600/20 border-pink-500/50' : 'bg-black/40 border-white/5'}`}>
                            <span className="text-[9px] font-mono opacity-50 w-4">{index + 1}</span>
                            <div className="flex-1 flex flex-col min-w-0">
                                <span className="text-[10px] font-bold truncate">{item.melodyId}</span>
                                <select 
                                    value={item.presetName}
                                    onChange={(e) => {
                                        const newPlaylist = [...config.playlist];
                                        const newPresetName = e.target.value;
                                        newPlaylist[index].presetName = newPresetName;
                                        
                                        if (config.playlistCurrentIndex === index) {
                                            let nextConfig = { ...config, playlist: newPlaylist };
                                            if (newPresetName !== 'Default') {
                                                const preset = PRESET_LIBRARY.find(p => p.name === newPresetName);
                                                if (preset) {
                                                    nextConfig = { ...nextConfig, ...preset.settings };
                                                }
                                            }
                                            setConfig(nextConfig);
                                        } else {
                                            setConfig(p => ({ ...p, playlist: newPlaylist }));
                                        }
                                    }}
                                    className="bg-black/40 text-[8px] text-white/70 border border-white/10 rounded px-1 outline-none cursor-pointer hover:bg-black/60 transition-colors"
                                >
                                    <option value="Default" className="bg-zinc-900 text-white">Default Preset</option>
                                    {PRESET_LIBRARY.map(p => (
                                        <option key={p.name} value={p.name} className="bg-zinc-900 text-white">{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-1">
                                <button 
                                    onClick={() => {
                                        const newPlaylist = [...config.playlist];
                                        if (index > 0) {
                                            [newPlaylist[index], newPlaylist[index-1]] = [newPlaylist[index-1], newPlaylist[index]];
                                            setConfig(p => ({ ...p, playlist: newPlaylist }));
                                        }
                                    }}
                                    className="text-white/40 hover:text-white"
                                >
                                    <i className="fa-solid fa-chevron-up text-[8px]"></i>
                                </button>
                                <button 
                                    onClick={() => {
                                        setConfig(p => ({
                                            ...p,
                                            playlist: p.playlist.filter((_, i) => i !== index)
                                        }));
                                    }}
                                    className="text-red-500 hover:text-red-400 p-1"
                                >
                                    <i className="fa-solid fa-trash text-[10px]"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="flex flex-col gap-3 mt-3 border-t border-white/5 pt-3">
                    <ControlGroup label="Continue Simulation" settingKey="rhythmicPlaylistContinueSimulation" darkMode={config.darkMode} tooltip="Prevent the ball from resetting when the next song in the playlist starts.">
                        <Toggle 
                            active={config.rhythmicPlaylistContinueSimulation} 
                            onClick={() => setConfig(p => ({...p, rhythmicPlaylistContinueSimulation: !p.rhythmicPlaylistContinueSimulation}))} 
                            color="bg-pink-600"
                        />
                    </ControlGroup>
                    
                    <SmartInput 
                        label="Inter-song Pause (ms)" 
                        min={0} max={10000} step={500}
                        value={config.playlistTransitionTime || 0} 
                        onChange={v => setConfig(p => ({...p, playlistTransitionTime: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="The pause duration between two songs. This affects the ball bounce duration during the transition."
                    />
                </div>

                <div className="flex items-center justify-between mt-1">
                    <ControlGroup label="Loop Playlist" settingKey="playlistLoop" darkMode={config.darkMode}>
                        <Toggle 
                            active={config.playlistLoop} 
                            onClick={() => setConfig(p => ({...p, playlistLoop: !p.playlistLoop}))} 
                            color="bg-pink-600"
                        />
                    </ControlGroup>
                    <button 
                        onClick={() => setConfig(p => ({ ...p, playlistCurrentIndex: 0 }))}
                        className="text-[8px] font-mono opacity-50 hover:opacity-100 underline"
                    >
                        Reset Index
                    </button>
                </div>
            </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
          <SectionLabel icon="fa-ruler-combined" darkMode={config.darkMode}>Sync Controls</SectionLabel>
          
          <SmartInput 
            label="Base Circle Size" 
            min={50} max={300} step={10}
            value={config.containerRadius} 
            onChange={v => setConfig(p => ({...p, containerRadius: v}))} 
            darkMode={config.darkMode} 
            tooltip="The initial size of the rhythmic arena."
            disabled={!isRhythmic}
            disabledMessage={rhythmicMsg}
            modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']}
          />
          <p className="text-[8px] opacity-50 text-right -mt-2">Sets the visual scale. Speed adapts automatically.</p>

          <SmartInput 
            label="Playback Speed" 
            min={0.1} max={5.0} step={0.1}
            value={config.rhythmicTimeScale || 1.0} 
            onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
            darkMode={config.darkMode} 
            tooltip="The speed multiplier for rhythmic playback (melody)."
            disabled={!isRhythmic}
            disabledMessage={rhythmicMsg}
            modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']}
          />
          <p className="text-[8px] opacity-50 text-right -mt-2">1.0 = Realtime. 0.5 = Half Speed.</p>

          {config.rhythmicBallSpeedDecoupled && (
            <>
              <SmartInput 
                label="Ball Visual Speed" 
                min={0.1} max={5.0} step={0.1}
                value={config.rhythmicBallSpeedScale || 1.0} 
                onChange={v => setConfig(p => ({...p, rhythmicBallSpeedScale: v}))} 
                darkMode={config.darkMode} 
                tooltip="Controls how fast the ball moves visually. High values make the arena bigger to maintain timing."
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']}
              />
              <p className="text-[8px] opacity-70 text-right -mt-2 text-pink-400 font-bold italic">Visual speed decoupled from melody.</p>
            </>
          )}

          <SmartInput 
            label="Sync Offset (ms)" 
            min={-500} max={500} step={1}
            value={config.rhythmicSyncOffset || 0} 
            onChange={v => setConfig(p => ({...p, rhythmicSyncOffset: v}))} 
            darkMode={config.darkMode} 
            tooltip="Manually adjust the timing of the sound relative to the bounce. Positive values delay the sound, negative values advance it."
            disabled={!isRhythmic}
            disabledMessage={rhythmicMsg}
            modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']}
          />
          <p className="text-[8px] opacity-50 text-right -mt-2">Adjust if sound feels slightly off-sync with the bounce.</p>

          <div className="grid grid-cols-2 gap-3 mt-1">
              <ControlGroup label="Decouple Ball Speed" settingKey="rhythmicBallSpeedDecoupled" disabled={!isRhythmic} disabledMessage={rhythmicMsg} modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']} darkMode={config.darkMode}>
                <Toggle 
                  active={config.rhythmicBallSpeedDecoupled} 
                  onClick={() => setConfig(p => ({...p, rhythmicBallSpeedDecoupled: !p.rhythmicBallSpeedDecoupled}))} 
                  color="bg-pink-600"
                />
              </ControlGroup>
              <ControlGroup label="Decouple Sync" settingKey="rhythmicDecoupleSpeed" disabled={!isRhythmic} disabledMessage={rhythmicMsg} modes={['RHYTHMIC', 'RHYTHMIC_ESCAPE']} darkMode={config.darkMode}>
                <Toggle 
                  active={config.rhythmicDecoupleSpeed} 
                  onClick={() => setConfig(p => ({...p, rhythmicDecoupleSpeed: !p.rhythmicDecoupleSpeed}))} 
                  color="bg-pink-600"
                />
              </ControlGroup>
          </div>

          <div className={`bg-black/20 p-3 rounded-xl border border-white/10 flex flex-col gap-2 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
            <p className="text-[10px] font-bold opacity-70 mb-1 uppercase tracking-widest">
                Max Size Sound Effect
            </p>
            
            <div className="flex gap-2 mb-2">
                {['SOUND_EFFECT', 'CUSTOM'].map(type => (
                    <button
                        key={type}
                        onClick={() => setConfig(p => ({ ...p, rhythmicMaxSizeSoundType: type as any }))}
                        className={`flex-1 py-1 rounded-lg border text-[9px] font-bold transition-all ${config.rhythmicMaxSizeSoundType === type ? 'bg-pink-600 border-pink-400 text-white' : 'bg-black/40 border-white/10 text-white/60 hover:bg-black/60'}`}
                    >
                        {type === 'SOUND_EFFECT' ? 'Built-in' : 'Custom'}
                    </button>
                ))}
            </div>

            {config.rhythmicMaxSizeSoundType === 'SOUND_EFFECT' ? (
                <select 
                    value={config.rhythmicMaxSizeSound}
                    onChange={(e) => setConfig(p => ({ ...p, rhythmicMaxSizeSound: e.target.value as any }))}
                    className={`w-full p-2 text-[10px] font-mono rounded-lg border outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
                >
                    {BALL_SOUND_EFFECTS.map(s => (
                        <option key={s} value={s} className="bg-neutral-900 text-white">{s}</option>
                    ))}
                </select>
            ) : (
                <select 
                    value={config.rhythmicMaxSizeCustomSoundId || ''}
                    onChange={(e) => setConfig(p => ({ ...p, rhythmicMaxSizeCustomSoundId: e.target.value }))}
                    className={`w-full p-2 text-[10px] font-mono rounded-lg border outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
                >
                    <option value="" className="bg-neutral-900 text-white">-- Select Custom Sound --</option>
                    {config.userSounds.map(s => (
                        <option key={s.id} value={s.id} className="bg-neutral-900 text-white">{s.name}</option>
                    ))}
                </select>
            )}
            <p className="text-[8px] opacity-50 text-right mt-1">Plays when ball reaches max size.</p>
          </div>

          <div className={`bg-black/20 p-3 rounded-xl border border-white/10 flex flex-col gap-3 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
            <p className="text-[10px] font-bold opacity-70 mb-2 uppercase tracking-widest">
                Ball Break Effect
            </p>
            
            <div className="grid grid-cols-2 gap-2">
                {['NONE', 'SHATTER', 'PARTICLES', 'EXPLODE'].map(effect => (
                    <button
                        key={effect}
                        onClick={() => setConfig(p => ({ ...p, rhythmicBreakEffect: effect as any }))}
                        className={`p-2 rounded-lg border text-[10px] font-bold transition-all ${config.rhythmicBreakEffect === effect ? 'bg-pink-600 border-pink-400 text-white' : 'bg-black/40 border-white/10 text-white/60 hover:bg-black/60'}`}
                    >
                        {effect}
                    </button>
                ))}
            </div>

            <div className="bg-black/20 p-2 rounded-lg border border-white/5">
                <p className="text-[9px] font-mono opacity-70 mb-2 uppercase tracking-widest">Outer Layer Break Animation</p>
                <select 
                    value={config.rhythmicOuterLayerAnimation}
                    onChange={(e) => setConfig(p => ({ ...p, rhythmicOuterLayerAnimation: e.target.value }))}
                    className={`w-full p-2 text-[10px] font-mono rounded-lg border outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
                >
                    {breakAnims.map(anim => (
                        <option key={anim} value={anim} className="bg-neutral-900 text-white">{anim.replace(/_/g, ' ')}</option>
                    ))}
                </select>
            </div>

            <SmartInput 
                label="Piece Count" 
                min={5} max={100} step={5}
                value={config.rhythmicBreakPieceCount} 
                onChange={v => setConfig(p => ({...p, rhythmicBreakPieceCount: v}))} 
                darkMode={config.darkMode} 
                tooltip="The number of fragments created when the ball breaks."
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC']}
            />

            <SmartInput 
                label="Gravity" 
                min={0} max={2} step={0.1}
                value={config.rhythmicBreakGravity} 
                onChange={v => setConfig(p => ({...p, rhythmicBreakGravity: v}))} 
                darkMode={config.darkMode} 
                tooltip="The downward force applied to ball fragments."
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC']}
            />

            <SmartInput 
                label="Reset Delay (ms)" 
                min={1000} max={10000} step={500}
                value={config.rhythmicBreakResetDelay} 
                onChange={v => setConfig(p => ({...p, rhythmicBreakResetDelay: v}))} 
                darkMode={config.darkMode} 
                tooltip="How long to wait before resetting the ball after it breaks."
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC']}
            />
          </div>

          <div className={`bg-black/20 p-3 rounded-xl border border-white/10 flex flex-col gap-3 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
            <div className="flex items-center justify-between mb-1">
              <SectionLabel icon="fa-wave-square" darkMode={config.darkMode} tooltip="Visualizes audio frequencies at the point of collision.">Audio Spectrum (Collision)</SectionLabel>
              <Toggle 
                active={config.rhythmicSpectrumEnabled} 
                onClick={() => setConfig(p => ({...p, rhythmicSpectrumEnabled: !p.rhythmicSpectrumEnabled}))} 
                color="bg-pink-600"
              />
            </div>
            
            {config.rhythmicSpectrumEnabled && (
                <>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        {['BALL', 'RAINBOW', 'CUSTOM', 'RGB_PULSE', 'HUE_SHIFT'].map(mode => (
                            <button
                                key={mode}
                                onClick={() => setConfig(p => ({ ...p, rhythmicSpectrumColorMode: mode as any }))}
                                className={`py-1 rounded-lg border text-[9px] font-bold transition-all ${config.rhythmicSpectrumColorMode === mode ? 'bg-pink-600 border-pink-400 text-white' : 'bg-black/40 border-white/10 text-white/60 hover:bg-black/60'}`}
                            >
                                {mode.replace(/_/g, ' ')}
                            </button>
                        ))}
                    </div>

                    {config.rhythmicSpectrumColorMode === 'CUSTOM' && (
                        <div className="flex items-center gap-2 mb-2">
                            <input 
                                type="color" 
                                value={config.rhythmicSpectrumCustomColor}
                                onChange={(e) => setConfig(p => ({ ...p, rhythmicSpectrumCustomColor: e.target.value }))}
                                className="w-8 h-8 rounded-lg bg-transparent border-none cursor-pointer"
                            />
                            <span className="text-[10px] font-mono opacity-70">{config.rhythmicSpectrumCustomColor}</span>
                        </div>
                    )}

                    <SmartInput 
                        label="Bar Count" 
                        min={8} max={256} step={8}
                        value={config.rhythmicSpectrumBarCount} 
                        onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarCount: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="The number of bars in the audio spectrum."
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />

                    <div className="grid grid-cols-2 gap-2">
                        <SmartInput 
                            label="Min Height" 
                            min={0} max={100} step={5}
                            value={config.rhythmicSpectrumMinHeight} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumMinHeight: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="The minimum height of the spectrum bars."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                        <SmartInput 
                            label="Max Height" 
                            min={20} max={300} step={10}
                            value={config.rhythmicSpectrumMaxHeight} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumMaxHeight: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="The maximum height of the spectrum bars."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                    </div>

                    <SmartInput 
                        label="Bar Width" 
                        min={1} max={20} step={1}
                        value={config.rhythmicSpectrumBarWidth} 
                        onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarWidth: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="The width of each spectrum bar."
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />

                    <SmartInput 
                        label="Bar Spacing" 
                        min={0} max={10} step={1}
                        value={config.rhythmicSpectrumBarSpacing} 
                        onChange={v => setConfig(p => ({...p, rhythmicSpectrumBarSpacing: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="The gap between spectrum bars."
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />

                    <div className="grid grid-cols-2 gap-2">
                        <SmartInput 
                            label="Grow Rate" 
                            min={0.01} max={1.0} step={0.01}
                            value={config.rhythmicSpectrumGrowRate} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumGrowRate: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="How fast the bars respond to audio peaks."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                        <SmartInput 
                            label="Shrink Rate" 
                            min={0.01} max={1.0} step={0.01}
                            value={config.rhythmicSpectrumShrinkRate} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumShrinkRate: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="How fast the bars return to their minimum height."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <SmartInput 
                            label="Audio Sens." 
                            min={0} max={5.0} step={0.1}
                            value={config.rhythmicSpectrumAudioSensitivity} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumAudioSensitivity: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="Multiplies the audio input intensity."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                        <ControlGroup label="Global Audio" settingKey="rhythmicSpectrumGlobalAudio" darkMode={config.darkMode} tooltip="When enabled, audio frequencies are shown on all bars, not just those recently hit.">
                            <div className="flex items-center justify-end border border-white/10 p-2 rounded-xl bg-black/20">
                                <Toggle active={config.rhythmicSpectrumGlobalAudio} onClick={() => setConfig(p => ({...p, rhythmicSpectrumGlobalAudio: !p.rhythmicSpectrumGlobalAudio}))} color="bg-pink-600" />
                            </div>
                        </ControlGroup>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <SmartInput 
                            label="Spatiality" 
                            min={0} max={1.0} step={0.1}
                            value={config.rhythmicSpectrumAudioSpatiality} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumAudioSpatiality: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="Controls how much the audio affects bars further from the collision point."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                        <SmartInput 
                            label="Bounce Int." 
                            min={0} max={5.0} step={0.1}
                            value={config.rhythmicSpectrumCollisionIntensity} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumCollisionIntensity: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="How much the physical collision affects the spectrum."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <SmartInput 
                            label="Bounce Spread" 
                            min={1} max={50} step={1}
                            value={config.rhythmicSpectrumCollisionSpread} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumCollisionSpread: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="How many bars are affected by a single collision."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                        <SmartInput 
                            label="Duration (ms)" 
                            min={200} max={5000} step={100}
                            value={config.rhythmicSpectrumDuration} 
                            onChange={v => setConfig(p => ({...p, rhythmicSpectrumDuration: v}))} 
                            darkMode={config.darkMode} 
                            tooltip="How long the spectrum remains visible after a collision."
                            disabled={!isRhythmic}
                            disabledMessage={rhythmicMsg}
                            modes={['RHYTHMIC']}
                        />
                    </div>
                </>
            )}
            <p className="text-[8px] opacity-50">Visualizes frequencies only at the point of recent collisions, growing outwards. Bars remain hidden until a bounce occurs.</p>
          </div>

          <div className={`bg-black/20 p-3 rounded-xl border border-white/10 flex flex-col gap-2 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
            <p className="text-[10px] font-bold opacity-70 mb-1 uppercase tracking-widest">
                Sync Logic Mode
            </p>
            <div className="flex flex-col gap-2">
                {[
                    { id: 'RADIUS_AND_SPEED', label: 'Radius + Speed (Dynamic)', desc: 'Adjusts both outer size and ball speed.' },
                    { id: 'SPEED_ONLY', label: 'Speed Only (Fixed Radius)', desc: 'Keeps outer size fixed, adjusts ball speed.' },
                    { id: 'DIRECTION_ONLY', label: 'Direction Only (Fixed Speed)', desc: 'Keeps speed fixed, adjusts bounce direction.' },
                    { id: 'CURVED_PATH', label: 'Curved Path (Fixed Radius)', desc: 'Bounces in arcs around the circle.' }
                ].map(mode => (
                    <button
                        key={mode.id}
                        onClick={() => setConfig(p => ({ ...p, rhythmicSyncMode: mode.id as any }))}
                        className={`text-left p-2 rounded-lg border transition-all ${config.rhythmicSyncMode === mode.id ? 'bg-pink-600 border-pink-400 text-white' : 'bg-black/40 border-white/10 text-white/60 hover:bg-black/60'}`}
                    >
                        <div className="text-[10px] font-bold">{mode.label}</div>
                        <div className="text-[8px] opacity-70">{mode.desc}</div>
                    </button>
                ))}
            </div>
          </div>

          <div className={`bg-black/20 p-3 rounded-xl border border-white/10 flex flex-col gap-3 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
            <p className="text-[10px] font-bold opacity-70 mb-1 uppercase tracking-widest">
                Physics & Path
            </p>
            
            <div className="grid grid-cols-2 gap-3">
                <SmartInput 
                    label="Min Speed Cap" 
                    min={0} max={50} step={1}
                    value={config.rhythmicMinSpeed} 
                    onChange={v => setConfig(p => ({...p, rhythmicMinSpeed: v}))} 
                    darkMode={config.darkMode} 
                    tooltip="Ensures the ball never moves slower than this value (spatial speed)."
                    disabled={!isRhythmic}
                    disabledMessage={rhythmicMsg}
                    modes={['RHYTHMIC']}
                />
                <SmartInput 
                    label="Max Speed Cap" 
                    min={10} max={500} step={10}
                    value={config.rhythmicMaxSpeed} 
                    onChange={v => setConfig(p => ({...p, rhythmicMaxSpeed: v}))} 
                    darkMode={config.darkMode} 
                    tooltip="Ensures the ball never moves faster than this value (spatial speed)."
                    disabled={!isRhythmic}
                    disabledMessage={rhythmicMsg}
                    modes={['RHYTHMIC']}
                />
            </div>

            <SmartInput 
                label="Min Bounce Distance" 
                min={0} max={500} step={10}
                value={config.rhythmicMinBounceDistance} 
                onChange={v => setConfig(p => ({...p, rhythmicMinBounceDistance: v}))} 
                darkMode={config.darkMode} 
                tooltip="Prevents 'sliding' by forcing a minimum travel distance between bounces."
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC']}
            />
            <p className="text-[8px] opacity-50 -mt-2">Prevents "sliding" by forcing a minimum travel distance between bounces.</p>

            {config.rhythmicSyncMode === 'CURVED_PATH' && (
                <>
                    <SmartInput 
                        label="Curve Depth" 
                        min={0.1} max={1.0} step={0.1}
                        value={config.rhythmicCurveDepth} 
                        onChange={v => setConfig(p => ({...p, rhythmicCurveDepth: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="How deep the curve goes towards the center (1.0 = center)."
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />
                    <p className="text-[8px] opacity-50 -mt-2">How deep the curve goes towards the center (1.0 = center).</p>

                    <SmartInput 
                        label="Speed Profile" 
                        min={0} max={2.0} step={0.1}
                        value={config.rhythmicCurveSpeedProfile} 
                        onChange={v => setConfig(p => ({...p, rhythmicCurveSpeedProfile: v}))} 
                        darkMode={config.darkMode} 
                        tooltip="Slow-Fast-Slow effect intensity (0 = constant speed)."
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />
                    <p className="text-[8px] opacity-50 -mt-2">Slow-Fast-Slow effect intensity (0 = constant speed).</p>
                </>
            )}
          </div>
      </div>
    </div>
  );
};
